<template>
  <div id="chatbot-page">
    <chatbot-sidebar
      :chatHistory="chatHistory"
      :newChatButtonDisabled="newChatButtonDisabled"
      @select-chat="loadChatSession"
      @new-chat="newChat"
    />
    <main class="chatbot-main">
      <div class="panel panel-default content-with-spacing">
        <div class="panel-body chatbot-panel-body">
          <div class="chatbot-conversation">
            <div class="chatbot-welcome">
              <img src="../../../img/icons/kate-bot-chat-colours.svg" alt="KATE Logo" />
              <h2>KATE Learning Assistant</h2>
              <p v-html="welcomeMessage.text"></p>
            </div>
            <div v-if="hasChatHistory" class="chat-messages">
              <k-chat-msg
                v-for="(message, index) in messages"
                :key="message.chatbot_query_feedback_id || index"
                :message="message"
                :showActions="message.fromChatbot && !message.isError"
                @update-rating="(rating) => updateRating(index, rating)"
              />
            </div>
            <typing-loader v-if="isLoadingReply"></typing-loader>
          </div>
          <div class="chatbot-input-footer">
            <k-ai-persistent-disclosure placement="above-input" />
            <form class="chatbot-input-container">
            <label for="chatbot-input">
              <textarea id="chatbot-input"
                class="form-control message-input"
                v-model="text"
                placeholder="Ask me anything..."
                rows="3"
                @keydown.enter="handleEnter"
                @input="autoExpand"
                ref="messageInput"
                :maxlength="maxQueryLength"
              ></textarea>
              <k-character-count :value-length="text.length" :max-length="maxQueryLength"></k-character-count>
            </label>
            <button :disabled='!text.trim() || isDisabled' type="submit" class="btn btn-primary submit-button" @click.prevent="postQuery">Send</button>
            </form>
          </div>
        </div>
      </div>
    </main>
  </div>
</template>

<style scoped>
#chatbot-page {
  display: flex;
  flex-wrap: nowrap;
  flex-direction: column;
  align-items: flex-start;
  padding-left: 310px;
  margin-top: 30px;
}

#chatbot-page main {
  padding: 15px 0;
  width: 100%;
}

.chatbot-panel-body {
  display: flex;
  flex-direction: column;
}

.chatbot-conversation {
  flex: 0 1 auto;
  max-height: 55vh;
  overflow-y: auto;
}

.chatbot-input-footer {
  flex-shrink: 0;
  padding-top: 8px;
}

.chatbot-welcome {
  text-align: center;
}

.chatbot-welcome p {
  text-align: left;
  margin: 20px 8px;
}

.chatbot-welcome img {
  width: 100px;
  height: 100px;
}

/* Chat history */
aside {
  padding: 15px;
}

/* Chat input */
.chatbot-input-container {
  display: flex;
  flex-direction: row;
  gap: 10px;
  width: 100%;
  flex-wrap: wrap;
  justify-content: flex-end;
  margin-top: 8px;
}

textarea.message-input {
  margin: 0;
  resize: vertical;
  display: flex;
  overflow: hidden;
  border-radius: 8px;
  box-shadow: unset;
  color: var(--kate-type-primary);
  flex-grow: 1;
}

.chatbot-input-container label,
.chatbot-input-container textarea {
  width: 100%;
  margin: 0;
}

/* Send button */
.submit-button {
  padding: 8px 40px;
}

.submit-button:hover:disabled,
.submit-button:disabled {
  color: var(--kate-disabled);
  background-color: transparent;
  text-decoration: none;
  font-weight: normal;
}

.submit-button:hover {
  text-decoration: underline;
  color: var(--kate-type-accent);
}
</style>

<script>
import StorageMixin from '@mixins/storage-mixins';
import useGlobalStore from '@stores/global';
import KAiPersistentDisclosure from '@base-components/ai/k-ai-persistent-disclosure.vue';
import KChatMsg from '@base-components/chatbot/k-chat-msg.vue';
import TypingLoader from '@base-components/chatbot/typing-loader.vue';
import KCharacterCount from '@base-components/forms/k-character-count.vue';
import { uuid } from '@utils/uuid-wrapper';
import PageReadyMixin from '@mixins/page-ready-mixin';
import ChatbotSidebar from './chatbot-sidebar.vue';

export default {
  mixins: [StorageMixin, PageReadyMixin],

  components: {
    ChatbotSidebar,
    KChatMsg,
    TypingLoader,
    KCharacterCount,
    KAiPersistentDisclosure,
  },

  data() {
    return {
      store: useGlobalStore(),
      text: '',
      chatbotResponse: undefined,
      welcomeMessage:
        {
          text: 'Hi there, I\'m KATE! I can answer questions about your programme, explain concepts, help you with a coding task and more. <br />What can I help you with today?',
          fromChatbot: true,
        },
      messages: [],
      isLoadingReply: false,
      isDisabled: false,
      sessionId: uuid(),
      chatHistory: [],
      defaultErrorMessage: 'There was an error getting a response from the Chatbot. Please try again later.',
      maxQueryLength: 2048, // Maximum length for the query
      isResolving: false,
      chatHistoryLoaded: false,
      postingQuery: false,
    };
  },

  watch: {
    '$route.params.sessionId': {
      immediate: true,
      handler(newSessionId) {
        if (newSessionId && newSessionId !== this.sessionId) {
          this.loadChatSession({ session_id: newSessionId });
        }
      },
    },
  },

  beforeMount() {
    this.$Loading.start();
    this.getChatHistory();
  },

  computed: {
    hasChatHistory() {
      return this.messages.length > 0;
    },

    programmeId() {
      if (this.$route.params.programmeId === undefined || Number.isNaN(Number(this.$route.params.programmeId))) {
        return undefined;
      }
      return Number(this.$route.params.programmeId);
    },

    newChatButtonDisabled() {
      return !this.hasChatHistory || this.isLoadingReply;
    },

    ready() {
      return this.chatHistoryLoaded && !this.postingQuery;
    },
  },

  methods: {
    handleEnter(event) {
      if (event.shiftKey) {
        return; // Allow new line
      }
      event.preventDefault();
      this.postQuery();
    },

    resolveSessionId() {
      if (this.isResolving) return;
      this.isResolving = true;

      // If no sessionId in route, generate a new one
      if (!this.$route.params.sessionId) {
        this.$router.replace({
          name: 'chatbot_page',
          params: {
            programmeId: this.programmeId,
            sessionId: this.sessionId,
          },
        });
      }

      this.isResolving = false;
    },

    getChatHistory() {
      this.chatHistoryLoaded = false;
      this.$http.get('/api/chatbot/sessions').then(result => {
        this.chatHistory = result.data;
        // Only update URL if we don't have a sessionId
        if (!this.$route.params.sessionId) {
          this.$router.replace({
            name: 'chatbot_page',
            params: {
              programmeId: this.programmeId,
              sessionId: this.sessionId,
            },
          });
        }
      }).catch(err => {
        this.$logger.error('Error getting chat history', undefined, err);
        this.chatHistory = [];
      }).then(() => {
        this.chatHistoryLoaded = true;
      });
    },

    postQuery() {
      this.postingQuery = true;
      this.isDisabled = true;
      this.messages.push({ text: this.text, fromChatbot: false });
      this.$logger.info('Posting query to chatbot', { programmeId: this.programmeId }, true);
      const payload = {
        query: this.text,
        page_url: this.$route.path,
        session_id: this.sessionId,
        programme_id: this.programmeId || null,
        history: this.messages.slice(0, -1).map(message => ({
          source: message.fromChatbot ? 'chatbot' : 'human',
          content: message.text,
        })),
        module_id: null,
        module_asset_id: null,
        asset_type: null,
        user_rating: null,
      };
      this.isLoadingReply = true;
      this.text = '';

      this.$http.post(`/api/chatbot/query/${this.programmeId}`, payload).then(result => {
        this.chatbotResponse = result.data;
        this.messages.push({
          ...this.chatbotResponse,
          text: this.chatbotResponse?.answer,
          fromChatbot: true,
        });
        this.$logger.info('Successfully posted query', { programmeId: this.programmeId });
        this.getChatHistory();
      }).catch(err => {
        const errorMessage = err.response?.status === 429 ? 'KATE is receiving a lot of requests right now. Please try again later.' : this.defaultErrorMessage;
        this.$logger.error('Error posting query', { programmeId: this.programmeId }, err);
        this.messages.push({
          text: errorMessage,
          isError: true,
          fromChatbot: true,
        });
      }).then(() => {
        this.isLoadingReply = false;
        this.isDisabled = false;
        this.postingQuery = false;
      });
    },

    loadChatSession(selectedChat) {
      this.chatHistoryLoaded = false;
      this.sessionId = selectedChat.session_id;
      this.$http.get(`/api/chatbot/sessions/${this.sessionId}`).then(result => {
        this.$router.push({
          name: 'chatbot_page',
          params: {
            programmeId: this.programmeId,
            sessionId: this.sessionId,
          },
        });
        this.messages = result.data.flatMap(item => [
          {
            text: item.query,
            fromChatbot: false,
          },
          {
            text: item.response,
            fromChatbot: true,
            chatbot_query_feedback_id: item.id,
            source_documents: item.source_documents,
            source_urls: item.source_urls,
            rating: item.user_rating,
          },
        ]);
      }).catch(err => {
        this.$logger.error('Error loading chat session', { sessionId: selectedChat.session_id }, err);
        this.$ktoast.showError('Something went wrong.');
        this.messages.push({
          text: 'There was an error getting a response from the Chatbot. Please try again later.',
          isError: true,
          fromChatbot: true,
        });
      }).then(() => {
        this.chatHistoryLoaded = true;
      });
    },

    updateRating(index, rating) {
      this.messages[index].rating = rating;
    },

    newChat() {
      this.messages = []; // This will trigger the messages watcher and set hasChatHistory to false
      this.sessionId = uuid(); // Generate new session ID
      this.$router.push({
        name: 'chatbot_page',
        params: {
          programmeId: this.programmeId,
          sessionId: this.sessionId,
        },
      });
      this.text = ''; // Clear any existing input
      this.chatbotResponse = undefined; // Reset chatbot response
    },

    autoExpand(e) {
      const textarea = e.target;
      const maxHeight = 300;

      // Reset height to allow proper calculation
      textarea.style.height = 'auto';

      // Set new height
      const newHeight = Math.min(textarea.scrollHeight, maxHeight);
      textarea.style.height = `${newHeight}px`;

      // Add scrollbar if content exceeds max height
      textarea.style.overflowY = newHeight === maxHeight ? 'auto' : 'hidden';
    },
  },
};
</script>
